import { CheckOutlined, EditOutlined, FileOutlined } from "@ant-design/icons";
import { Badge, Button, Col, Descriptions, FloatButton, Row, Space, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ContentHeader } from "../../components";
import { responseStatus, urlAdmin } from "../../constants";
import { Links } from "../../helpers/Links";
import { menuHelper } from "../../helpers/account";
import { ChiTietThanhLyTaiSan, TrangThaiThanhLyTaiSan } from "../../models/taisan/ThanhLyTaiSan";
import { TaiSan, TrangThaiTaiSan } from "../../models/taisan/TaiSan";
import LoadingDetailPage from "../../modules/loading/LoadingDetailPage";
import { hoanThanhThanhLy, layChiTietPhieuThanhLy } from "../../services/thanhlytaisan";

const XemThanhLyTaiSan = () => {
    const navigate = useNavigate();
    const params = new URLSearchParams(window.location.search);
    const id = params.get("id");
    const menu: any = menuHelper.checkMultiPermission([Links.ThanhLyTaiSan.ChiTiet, Links.ThanhLyTaiSan.Xem]);

    const [isLoading, setIsLoading] = useState(false);
    const [phieu, setPhieu] = useState<ChiTietThanhLyTaiSan | null>(null);

    const loadChiTiet = async () => {
        if (!id) return;

        try {
            setIsLoading(true);
            const result = await layChiTietPhieuThanhLy(parseInt(id));
            setPhieu(result);
        } catch (error: any) {
            toast.error("Lỗi khi tải phiếu thanh lý: " + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadChiTiet();
    }, [id]);

    const getTrangThaiPhieuTag = (value: number) => {
        switch (value) {
            case TrangThaiThanhLyTaiSan.DuThao:
                return <Tag>Dự thảo</Tag>;
            case TrangThaiThanhLyTaiSan.HoanThanh:
                return <Tag color="green">Hoàn thành</Tag>;
            default:
                return <Tag>Không xác định</Tag>;
        }
    };

    const getTrangThaiTaiSanTag = (value: number) => {
        switch (value) {
            case TrangThaiTaiSan.MoiNhap:
                return <Tag color="blue">Mới nhập</Tag>;
            case TrangThaiTaiSan.DangSuDung:
                return <Tag color="green">Đang sử dụng</Tag>;
            case TrangThaiTaiSan.BaoTri:
                return <Tag color="orange">Bảo trì</Tag>;
            case TrangThaiTaiSan.Hong:
                return <Tag color="red">Hỏng</Tag>;
            case TrangThaiTaiSan.ChoThanhLy:
                return <Tag color="purple">Chờ thanh lý</Tag>;
            case TrangThaiTaiSan.DieuChuyen:
                return <Tag color="cyan">Điều chuyển</Tag>;
            case TrangThaiTaiSan.DaThanhLy:
                return <Tag color="red">Đã thanh lý</Tag>;
            default:
                return <Tag>Không xác định</Tag>;
        }
    };




    const handleHoanThanh = async () => {
        if (!id) return;

        try {
            setIsLoading(true);
            const response = await hoanThanhThanhLy(parseInt(id));
            if (response.trangThai !== responseStatus.thanhCong) {
                throw new Error(response.thongBao);
            }
            toast.success("Hoàn thành thanh lý tài sản thành công");
            loadChiTiet();
        } catch (error: any) {
            toast.error("Lỗi khi hoàn thành thanh lý: " + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading && !phieu) {
        return <LoadingDetailPage title="Chi tiết phiếu thanh lý tài sản" />;
    }

    const columnsTaiSan = [
        {
            title: "STT",
            key: "stt",
            width: 60,
            align: "center" as const,
            render: (_: any, __: any, index: number) => index + 1,
        },
        {
            title: "Tên công cụ dụng cụ",
            dataIndex: "tenTaiSan",
            key: "tenTaiSan",
            width: 220,
        },
        {
            title: "Mã công cụ dụng cụ",
            dataIndex: "ma",
            key: "ma",
            width: 140,
        },
        {
            title: "Nguyên giá (đồng)",
            key: "nguyenGia",
            width: 140,
            align: "right" as const,
            render: (_: any, record: TaiSan) => ((record.nguyenGia ?? record.giaTriKhiNhap ?? 0).toLocaleString("vi-VN")),
        },
        {
            title: "Giá trị còn lại",
            dataIndex: "giaTriConLai",
            key: "giaTriConLai",
            width: 140,
            align: "right" as const,
            render: (value: number) => (value ?? 0).toLocaleString("vi-VN"),
        },
        {
            title: "Số lượng",
            dataIndex: "soLuong",
            key: "soLuong",
            width: 90,
            align: "right" as const,
            render: (value: number) => value ?? 1,
        },
        {
            title: "ĐVT",
            dataIndex: "donViTinh",
            key: "donViTinh",
            width: 80,
            align: "center" as const,
            render: (value: string) => value || "Cái",
        },
        {
            title: "Ghi chú",
            key: "ghiChu",
            width: 180,
            render: (_: any, record: TaiSan) => record.ghiChu || record.moTa || "—",
        },
    ];

    return (
        <section style={{ paddingBottom: 52 }}>
            <ContentHeader title="Chi tiết phiếu thanh lý tài sản" />
            <Row>
                <Col span={24}>
                    {phieu && (
                        <div className="card">
                            <div className="card-body">
                                <h5 className="mb-3">Thông tin phiếu thanh lý</h5>
                                <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
                                    <Descriptions.Item label="Ngày lập">
                                        {dayjs(phieu.ngayLap).format("DD/MM/YYYY")}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Trạng thái">
                                        {getTrangThaiPhieuTag(phieu.trangThai)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Số file đính kèm">
                                        {phieu.dsTaiLieu?.length || 0}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Số tài sản">
                                        {phieu.danhSachTaiSan?.length || 0}
                                    </Descriptions.Item>
                                </Descriptions>

                                <div style={{ marginTop: 24 }}>
                                    <h5 className="mb-3">Nội dung phiếu thanh lý</h5>
                                    <div className="document" style={{ border: "1px solid #f0f0f0", padding: "16px", borderRadius: "4px", backgroundColor: "#fff" }}>
                                        {phieu.noiDung ? (
                                            <div dangerouslySetInnerHTML={{ __html: phieu.noiDung }} />
                                        ) : <div style={{ fontStyle: "italic", color: "#888" }}>Chưa có nội dung phiếu thanh lý</div>}
                                    </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 24, marginBottom: 8 }}>
                                    <h5 style={{ margin: 0 }}>Danh sách tài sản</h5>
                                    <Badge count={phieu.danhSachTaiSan?.length || 0} style={{ backgroundColor: "#1677ff" }} />
                                </div>
                                <Table
                                    columns={columnsTaiSan}
                                    dataSource={phieu.danhSachTaiSan}
                                    rowKey={(record: TaiSan) => record.id!}
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: 1100 }}
                                    summary={(pageData) => {
                                        let totalNguyenGia = 0;
                                        let totalGiaTriConLai = 0;
                                        let totalSoLuong = 0;

                                        pageData.forEach((item) => {
                                            totalNguyenGia += (item.nguyenGia ?? item.giaTriKhiNhap ?? 0);
                                            totalGiaTriConLai += (item.giaTriConLai ?? 0);
                                            totalSoLuong += (item.soLuong ?? 1);
                                        });

                                        return (
                                            <Table.Summary.Row style={{ fontWeight: 'bold', background: '#fafafa' }}>
                                                <Table.Summary.Cell index={0} colSpan={3} align="center">
                                                    Tổng cộng:
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={1} align="right">
                                                    {totalNguyenGia.toLocaleString('vi-VN')}
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={2} align="right">
                                                    {totalGiaTriConLai.toLocaleString('vi-VN')}
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={3} align="right">
                                                    {totalSoLuong}
                                                </Table.Summary.Cell>
                                                <Table.Summary.Cell index={4} colSpan={2} />
                                            </Table.Summary.Row>
                                        );
                                    }}
                                />

                                {phieu.dsTaiLieu && phieu.dsTaiLieu.length > 0 && (
                                    <div style={{ marginTop: 24 }}>
                                        <h5 className="mb-3"><FileOutlined /> Tài liệu đính kèm</h5>
                                        <ul>
                                            {phieu.dsTaiLieu.map((file) => (
                                                <li key={file.idTaiLieu}>
                                                    <a href={urlAdmin + file.duongDan.replace(/\\/g, "/")} target="_blank" rel="noopener noreferrer">
                                                        {file.tenTep}
                                                    </a>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                <div className="mt-3 d-flex justify-content-end">
                                    <Space>
                                        {menu[Links.ThanhLyTaiSan.ChiTiet] && phieu.trangThai !== TrangThaiThanhLyTaiSan.HoanThanh && (
                                            <Button type="primary" loading={isLoading} disabled={(phieu.dsTaiLieu?.length || 0) <= 0} onClick={handleHoanThanh}>
                                                Hoàn thành thanh lý
                                            </Button>
                                        )}
                                    </Space>
                                </div>
                            </div>
                        </div>
                    )}
                </Col>
            </Row>

            <Row className="mt-1 mb-1">
                <Col span={24}>
                    {menu[Links.ThanhLyTaiSan.ChiTiet] && phieu?.trangThai !== TrangThaiThanhLyTaiSan.HoanThanh && (
                        <FloatButton.Group trigger="hover" type="primary" style={{ right: 50, bottom: 80 }}>
                            <FloatButton
                                tooltip="Chỉnh sửa"
                                className="hrm-btn-warning"
                                icon={<EditOutlined />}
                                onClick={() => navigate(`${Links.ThanhLyTaiSan.ChiTiet}?id=${id}`)}
                            />
                        </FloatButton.Group>
                    )}
                </Col>
            </Row>
        </section>
    );
};

export default XemThanhLyTaiSan;
